import { VehicleIcon, LocationIcon, ServicesIcon, OilIcon, WarningIcon, ClockIcon, EditIcon, DeleteIcon, SignalIcon, LinkIcon, CameraIcon } from "../../lib/cccIcons";
import useIsMobile from "../../lib/useIsMobile"
import { useEffect, useState } from "react"
import { supabase } from "../../lib/supabase"
import { useAuth } from "../../contexts/AuthContext"
import toast from "react-hot-toast"

export default function CustomerVehicles() {
  const isMobile = useIsMobile()
  const { user } = useAuth()
  const [vehicles, setVehicles] = useState([])
  const [form, setForm] = useState({make:"",model:"",year:"",color:"",license_plate:"",photo_url:"",current_mileage:"",last_service_date:"",last_oil_change_date:""})
  const [uploadingPhoto, setUploadingPhoto] = useState(false)
  const [editing, setEditing] = useState(null)
  const [loading, setLoading] = useState(true)
  const [trackers, setTrackers] = useState([])
  const [showTracker, setShowTracker] = useState(null)
  const [trackerForm, setTrackerForm] = useState({ provider:"cartrack", api_key:"", api_secret:"", vehicle_identifier:"" })
  const [savingTracker, setSavingTracker] = useState(false)

  useEffect(() => { if (user) load() }, [user])

  async function load() {
    const { data: trk } = await supabase.from("vehicle_trackers").select("*").eq("customer_id", user.id)
    setTrackers(trk||[])
    const { data } = await supabase.from("vehicles").select("*").eq("user_id", user.id).order("is_default",{ascending:false})
    setVehicles(data||[]); setLoading(false)
  }

  async function save(e) {
    e.preventDefault()
    const payload = {...form, year:parseInt(form.year), user_id:user.id, current_mileage: form.current_mileage?parseInt(form.current_mileage):null, last_service_date: form.last_service_date||null, last_oil_change_date: form.last_oil_change_date||null}
    if (editing) {
      const {error} = await supabase.from("vehicles").update(payload).eq("id",editing).eq("user_id",user.id)
      if (error) return toast.error(error.message)
      toast.success("Vehicle updated"); setEditing(null)
    } else {
      const {error} = await supabase.from("vehicles").insert(payload)
      if (error) return toast.error(error.message)
      toast.success("Vehicle added")
    }
    setForm({make:"",model:"",year:"",color:"",license_plate:""}); load()
  }

  async function remove(id) {
    if (!confirm("Remove this vehicle?")) return
    const { error } = await supabase.from("vehicles").delete().eq("id",id).eq("user_id",user.id)
    if (error) { toast.error(error.message); console.error("Delete error:", error); return }
    toast.success("Vehicle removed"); load()
  }

  async function setDefault(id) {
    await supabase.from("vehicles").update({is_default:false}).eq("user_id",user.id)
    await supabase.from("vehicles").update({is_default:true}).eq("id",id).eq("user_id",user.id)
    toast.success("Default vehicle set"); load()
  }

  function getMaintenanceStatus(v) {
    const alerts = []
    const today = new Date()
    if (v.last_service_date) {
      const monthsSince = (today - new Date(v.last_service_date)) / (1000*60*60*24*30)
      if (monthsSince >= 6) alerts.push({ type:"service", level:"overdue", text:"Full service overdue (6+ months)" })
      else if (monthsSince >= 5) alerts.push({ type:"service", level:"due-soon", text:"Full service due soon" })
    } else {
      alerts.push({ type:"service", level:"unknown", text:"No service history — add last service date" })
    }
    if (v.last_oil_change_date) {
      const monthsSince = (today - new Date(v.last_oil_change_date)) / (1000*60*60*24*30)
      if (monthsSince >= 3) alerts.push({ type:"oil", level:"overdue", text:"Oil change overdue (3+ months)" })
      else if (monthsSince >= 2.5) alerts.push({ type:"oil", level:"due-soon", text:"Oil change due soon" })
    }
    if (v.current_mileage && v.last_service_mileage) {
      const kmSince = v.current_mileage - v.last_service_mileage
      if (kmSince >= 10000) alerts.push({ type:"service-km", level:"overdue", text:"Service overdue (10,000+ km since last service)" })
      else if (kmSince >= 8000) alerts.push({ type:"service-km", level:"due-soon", text:"Service due soon (approaching 10,000km)" })
    }
    if (v.current_mileage && v.last_oil_change_mileage) {
      const kmSince = v.current_mileage - v.last_oil_change_mileage
      if (kmSince >= 5000) alerts.push({ type:"oil-km", level:"overdue", text:"Oil change overdue (5,000+ km since last)" })
      else if (kmSince >= 4000) alerts.push({ type:"oil-km", level:"due-soon", text:"Oil change due soon (approaching 5,000km)" })
    }
    return alerts
  }
  const inp = {width:"100%",background:"#ffffff",border:"1px solid #e5e5e5",borderRadius:8,padding:"10px 12px",color:"#000000",fontSize:13,outline:"none",marginBottom:10,fontFamily:"'DM Sans',sans-serif"}
  const lbl = {fontSize:11,color:"#666",textTransform:"uppercase",letterSpacing:"0.05em",display:"block",marginBottom:4}

  async function saveTracker(e) {
    e.preventDefault()
    if (!trackerForm.api_key) return
    setSavingTracker(true)
    const existing = trackers.find(t=>t.vehicle_id===showTracker?.id)
    if (existing) {
      await supabase.from("vehicle_trackers").update({
        provider: trackerForm.provider,
        api_key: trackerForm.api_key, api_secret: trackerForm.api_secret,
        vehicle_identifier: trackerForm.vehicle_identifier,
        is_active: true
      }).eq("id", existing.id)
    } else {
      await supabase.from("vehicle_trackers").insert({
        customer_id: user.id,
        vehicle_id: showTracker.id,
        provider: trackerForm.provider,
        api_key: trackerForm.api_key, api_secret: trackerForm.api_secret,
        vehicle_identifier: trackerForm.vehicle_identifier,
        is_active: true
      })
    }
    const { data: trk } = await supabase.from("vehicle_trackers").select("*").eq("customer_id", user.id)
    setTrackers(trk||[])
    setShowTracker(null)
    setSavingTracker(false)
    toast.success("Tracker linked successfully!")
  }

  async function removeTracker(vehicleId) {
    await supabase.from("vehicle_trackers").delete().eq("vehicle_id", vehicleId).eq("customer_id", user.id)
    const { data: trk } = await supabase.from("vehicle_trackers").select("*").eq("customer_id", user.id)
    setTrackers(trk||[])
    toast.success("Tracker removed")
  }

  return (
    <div>
      {loading&&<div style={{color:"#777777",fontSize:13}}>Loading...</div>}
      {vehicles.map(v=>(
        <div key={v.id} style={{background:"#ffffff",border:`1px solid ${v.is_default?"#e6821e40":"#eeeeee"}`,borderRadius:12,padding:"1rem",marginBottom:10}}>
          {/* Top row: photo + name + badge */}
          <div style={{display:"flex",alignItems:"flex-start",gap:12,marginBottom:10}}>
            <div style={{width:64,height:64,background:"#fff8f0",borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",fontSize:28,overflow:"hidden",flexShrink:0,border:"1px solid #e6821e20"}}>
              {v.photo_url ? <img src={v.photo_url} alt="Vehicle" style={{width:"100%",height:"100%",objectFit:"cover"}}/> : "🚗"}
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap",marginBottom:2}}>
                <div style={{fontFamily:"Syne",fontSize:15,fontWeight:800,color:"#000"}}>{v.make} {v.model}</div>
                {v.is_default&&<span style={{fontSize:10,color:"#e6821e",background:"#fff8f0",padding:"2px 8px",borderRadius:10,border:"1px solid #e6821e30",flexShrink:0}}>⭐ Default</span>}
              </div>
              <div style={{fontSize:12,color:"#666",marginBottom:2}}>{v.year} · {v.color||"No color"} · {v.license_plate}</div>
              {v.current_mileage&&<div style={{fontSize:11,color:"#888",display:"flex",alignItems:"center",gap:3}}><LocationIcon size={11} color="#888"/> {Number(v.current_mileage).toLocaleString()} km</div>}
              {v.last_service_date&&<div style={{fontSize:11,color:"#888",display:"flex",alignItems:"center",gap:3}}><ServicesIcon size={11} color="#888"/> Last service: {new Date(v.last_service_date).toLocaleDateString()}</div>}
              {v.last_oil_change_date&&<div style={{fontSize:11,color:"#888",display:"flex",alignItems:"center",gap:3}}><OilIcon size={11} color="#888"/> Last oil change: {new Date(v.last_oil_change_date).toLocaleDateString()}</div>}
            </div>
          </div>
          {/* Maintenance alerts */}
          {getMaintenanceStatus(v).filter(a=>a.level!=="unknown").length>0&&(
            <div style={{display:"flex",flexWrap:"wrap",gap:4,marginBottom:10}}>
              {getMaintenanceStatus(v).filter(a=>a.level!=="unknown").map((a,i)=>(
                <div key={i} style={{fontSize:10,color:a.level==="overdue"?"#e24b4a":"#e6821e",background:a.level==="overdue"?"#fff5f5":"#fff8f0",padding:"3px 10px",borderRadius:20,border:`1px solid ${a.level==="overdue"?"#e24b4a30":"#e6821e30"}`}}>
                  {a.level==="overdue"?<WarningIcon size={12} color="#e24b4a"/>:<ClockIcon size={12} color="#e6821e"/>} {a.text}
                </div>
              ))}
            </div>
          )}
          {/* Action buttons */}
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {!v.is_default&&<button onClick={()=>setDefault(v.id)} style={{background:"#fff8f0",border:"1px solid #e6821e40",borderRadius:7,color:"#e6821e",fontSize:11,padding:"5px 10px",cursor:"pointer"}}>⭐ Set default</button>}
            <button onClick={()=>{setEditing(v.id);setForm({make:v.make,model:v.model,year:String(v.year),color:v.color||"",license_plate:v.license_plate,photo_url:v.photo_url||"",current_mileage:v.current_mileage?String(v.current_mileage):"",last_service_date:v.last_service_date||"",last_oil_change_date:v.last_oil_change_date||""})}} style={{background:"#eff6ff",border:"1px solid #378add40",borderRadius:7,color:"#378add",fontSize:11,padding:"5px 10px",cursor:"pointer"}}>✏️ Edit</button>
            <button onClick={()=>remove(v.id)} style={{background:"#fff5f5",border:"1px solid #e24b4a40",borderRadius:7,color:"#e24b4a",fontSize:11,padding:"5px 10px",cursor:"pointer"}}>🗑️ Remove</button>
            {trackers.find(t=>t.vehicle_id===v.id)
              ? <button onClick={()=>setShowTracker(v)} style={{background:"#f0fdf4",border:"1px solid #1d9e7540",borderRadius:7,color:"#1d9e75",fontSize:11,padding:"5px 10px",cursor:"pointer"}}>📡 Tracker linked</button>
              : <button onClick={()=>setShowTracker(v)} style={{background:"#f8f8f8",border:"1px solid #ddd",borderRadius:7,color:"#555",fontSize:11,padding:"5px 10px",cursor:"pointer"}}>🔗 Link tracker</button>
            }
          </div>
        </div>
      ))}
      <div style={{background:"#ffffff",border:"1px solid #eeeeee",borderRadius:10,padding:"1.25rem",marginTop:"1rem"}}>
        <div style={{fontFamily:"Syne",fontSize:14,fontWeight:700,marginBottom:"1rem"}}>{editing?"Edit vehicle":"Add a vehicle"}</div>
        <form onSubmit={save}>
          <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:10}}>
            <div><label style={lbl}>Make</label><input style={inp} placeholder="Toyota" value={form.make} onChange={e=>setForm(f=>({...f,make:e.target.value}))} required/></div>
            <div><label style={lbl}>Model</label><input style={inp} placeholder="Camry" value={form.model} onChange={e=>setForm(f=>({...f,model:e.target.value}))} required/></div>
            <div><label style={lbl}>Year</label><input style={inp} placeholder="2020" type="number" min="1990" max="2026" value={form.year} onChange={e=>setForm(f=>({...f,year:e.target.value}))} required/></div>
            <div><label style={lbl}>Color</label><input style={inp} placeholder="Silver" value={form.color} onChange={e=>setForm(f=>({...f,color:e.target.value}))}/></div>
          </div>
          <div><label style={lbl}>License plate</label><input style={inp} placeholder="KDA 123A" value={form.license_plate} onChange={e=>setForm(f=>({...f,license_plate:e.target.value}))} required/></div>
          <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr 1fr",gap:10}}>
            <div><label style={lbl}>Current mileage (km)</label><input style={inp} placeholder="45000" type="number" value={form.current_mileage} onChange={e=>setForm(f=>({...f,current_mileage:e.target.value}))}/></div>
            <div><label style={lbl}>Last full service</label><input style={inp} type="date" value={form.last_service_date} onChange={e=>setForm(f=>({...f,last_service_date:e.target.value}))}/></div>
            <div><label style={lbl}>Last oil change</label><input style={inp} type="date" value={form.last_oil_change_date} onChange={e=>setForm(f=>({...f,last_oil_change_date:e.target.value}))}/></div>
          </div>
          <label style={lbl}>Vehicle photo (optional)</label>
          <div style={{ marginBottom:10 }}>
            {form.photo_url&&<img src={form.photo_url} alt="Vehicle" style={{ width:"100%", maxHeight:150, objectFit:"cover", borderRadius:8, marginBottom:8 }}/>}
            <label style={{ display:"block", width:"100%", background:uploadingPhoto?"#f5f5f5":"#ffffff", border:"2px dashed #e5e5e5", borderRadius:8, padding:"12px", color:"#555555", fontSize:12, marginBottom:8, cursor:"pointer", textAlign:"center" }}>
              {uploadingPhoto ? "⏳ Uploading..." : form.photo_url ? "📷 Tap to change photo" : "📷 Tap to upload vehicle photo"}
              <input type="file" accept="image/*" style={{ display:"none" }} onChange={async(e)=>{
                const file = e.target.files[0]
                if (!file) return
                if (file.size > 5*1024*1024) return toast.error("Photo must be under 5MB")
                if (!file.type.startsWith("image/")) return toast.error("Please select an image file")
                setUploadingPhoto(true)
                try {
                  const ext = file.name.split(".").pop()
                  const path = `vehicles/${user.id}/vehicle-${Date.now()}.${ext}`
                  const { error } = await supabase.storage.from("marketplace").upload(path, file, { upsert:true })
                  if (error) throw error
                  const { data } = supabase.storage.from("marketplace").getPublicUrl(path)
                  setForm(f=>({...f, photo_url:data.publicUrl}))
                  toast.success("Photo uploaded!")
                } catch(err) {
                  toast.error("Upload failed: " + err.message)
                } finally {
                  setUploadingPhoto(false)
                }
              }}/>
            </label>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button type="submit" style={{background:"#e6821e",border:"none",borderRadius:8,color:"#fff",fontFamily:"Syne,sans-serif",fontSize:13,fontWeight:700,padding:"10px 20px",cursor:"pointer"}}>{editing?"Update vehicle":"Add vehicle"}</button>
            {editing&&<button type="button" onClick={()=>{setEditing(null);setForm({make:"",model:"",year:"",color:"",license_plate:""})}} style={{background:"none",border:"1px solid #dddddd",borderRadius:8,color:"#555555",fontSize:13,padding:"10px 20px",cursor:"pointer"}}>Cancel</button>}
          </div>
        </form>
      </div>
      {showTracker&&(
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:200, display:"flex", alignItems:"flex-end", justifyContent:"center" }}
          onClick={e=>{ if(e.target===e.currentTarget) setShowTracker(null) }}>
          <div style={{ background:"#fff", borderRadius:"16px 16px 0 0", padding:"1.5rem", width:"100%", maxWidth:480 }}>
            <div style={{ fontFamily:"Syne", fontSize:15, fontWeight:800, marginBottom:4 }}>🔗 Link Tracker</div>
            <div style={{ fontSize:12, color:"#888", marginBottom:"1rem" }}>{showTracker?.make} {showTracker?.model} · {showTracker?.license_plate}</div>
            <div style={{ background:"#f0fdf4", border:"1px solid #1d9e7530", borderRadius:8, padding:"0.75rem", marginBottom:"1rem", fontSize:12, color:"#555", lineHeight:1.6 }}>
              💡 Enter your tracker API key from your tracker provider dashboard. CCC uses your own credentials — we never store location without your permission.
            </div>
            <form onSubmit={saveTracker}>
              <label style={{ fontSize:11, color:"#666", display:"block", marginBottom:4 }}>Tracker Provider</label>
              <select value={trackerForm.provider} onChange={e=>setTrackerForm(f=>({...f,provider:e.target.value}))}
                style={{ width:"100%", background:"#f5f5f5", border:"1px solid #e0e0e0", borderRadius:8, padding:"10px 12px", fontSize:13, marginBottom:10, outline:"none" }}>
                <option value="cartrack">Cartrack Kenya</option>
                <option value="itrack">iTrack (itrack.live)</option>
                <option value="itrace">iTrace Africa</option>
                <option value="other">Other (contact support)</option>
              </select>
              <label style={{ fontSize:11, color:"#666", display:"block", marginBottom:4 }}>API Key / Access Token *</label>
              <input value={trackerForm.api_key} onChange={e=>setTrackerForm(f=>({...f,api_key:e.target.value}))} placeholder="Enter your tracker API key / username"
                style={{ width:"100%", background:"#f5f5f5", border:"1px solid #e0e0e0", borderRadius:8, padding:"10px 12px", fontSize:13, marginBottom:10, outline:"none", boxSizing:"border-box" }} required/>
              {trackerForm.provider==="cartrack"&&(
                <><label style={{ fontSize:11, color:"#666", display:"block", marginBottom:4 }}>Password / API Secret *</label>
                <input value={trackerForm.api_secret||""} onChange={e=>setTrackerForm(f=>({...f,api_secret:e.target.value}))} placeholder="Your Cartrack password"
                  style={{ width:"100%", background:"#f5f5f5", border:"1px solid #e0e0e0", borderRadius:8, padding:"10px 12px", fontSize:13, marginBottom:10, outline:"none", boxSizing:"border-box" }}/></>
              )}
              <label style={{ fontSize:11, color:"#666", display:"block", marginBottom:4 }}>Vehicle ID (from tracker dashboard)</label>
              <input value={trackerForm.vehicle_identifier} onChange={e=>setTrackerForm(f=>({...f,vehicle_identifier:e.target.value}))} placeholder="e.g. VH-12345 or plate number"
                style={{ width:"100%", background:"#f5f5f5", border:"1px solid #e0e0e0", borderRadius:8, padding:"10px 12px", fontSize:13, marginBottom:16, outline:"none", boxSizing:"border-box" }}/>
              <div style={{ display:"flex", gap:8 }}>
                <button type="submit" disabled={savingTracker} style={{ flex:1, background:"#e6821e", border:"none", borderRadius:10, color:"#fff", fontFamily:"Syne,sans-serif", fontSize:13, fontWeight:700, padding:"12px", cursor:"pointer" }}>
                  {savingTracker?"Saving...":"Save tracker"}
                </button>
                {trackers.find(t=>t.vehicle_id===showTracker?.id)&&(
                  <button type="button" onClick={()=>removeTracker(showTracker.id)} style={{ background:"#fff5f5", border:"1px solid #e24b4a40", borderRadius:10, color:"#e24b4a", fontSize:13, padding:"12px 16px", cursor:"pointer" }}>Remove</button>
                )}
                <button type="button" onClick={()=>setShowTracker(null)} style={{ background:"#f5f5f5", border:"none", borderRadius:10, color:"#555", fontSize:13, padding:"12px 16px", cursor:"pointer" }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}





    </div>
  )
}


